import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api, { setTokens, clearTokens, getTokens } from '@/lib/api';

const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      activeOrgSlug: 'btcc',
      activeOrgId: 'org_btcc',
      mustChangePassword: false,
      forceMemberView: false, // admin can preview as member
      isGuest: false, // public viewer mode

      // Login with phone + password
      login: async (phone, password) => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.post('/auth/login', { phone, password });
          const { tokens, me, active_organization_id, must_change_password } = response.data;

          setTokens(tokens.access_token, tokens.refresh_token);

          set({
            user: me,
            isAuthenticated: true,
            isLoading: false,
            error: null,
            mustChangePassword: !!must_change_password,
            activeOrgId: active_organization_id || me?.default_org_id || 'org_btcc',
            activeOrgSlug: me?.memberships?.[0]?.slug || 'btcc',
          });

          return { success: true, mustChangePassword: !!must_change_password, userName: me?.player_full_name || me?.display_name || '' };
        } catch (error) {
          const message = error.response?.data?.detail || 'Invalid phone or password';
          set({ isLoading: false, error: message });
          return { success: false, error: message };
        }
      },

      // Change password
      changePassword: async (currentPassword, newPassword) => {
        set({ isLoading: true, error: null });
        try {
          await api.post('/auth/change-password', {
            current_password: currentPassword,
            new_password: newPassword,
          });
          set({ isLoading: false, mustChangePassword: false });
          return { success: true };
        } catch (error) {
          const message = error.response?.data?.detail || 'Failed to change password';
          set({ isLoading: false, error: message });
          return { success: false, error: message };
        }
      },

      // Fetch current user from /auth/me — ALWAYS calls API to ensure activeOrgSlug is fresh
      fetchMe: async () => {
        const { accessToken } = getTokens();
        if (!accessToken) {
          set({ isAuthenticated: false, user: null, isLoading: false });
          return;
        }

        // Do NOT short-circuit on persisted user data — always refresh from backend
        // to ensure activeOrgSlug is correct (stale persist causes "org not found")
        set({ isLoading: true });
        try {
          const response = await api.get('/auth/me');
          const me = response.data;
          set({
            user: me,
            isAuthenticated: true,
            isLoading: false,
            activeOrgId: me?.default_org_id || 'org_btcc',
            activeOrgSlug: me?.memberships?.[0]?.slug || 'btcc',
          });
          return me;
        } catch (error) {
          if (!error.response) {
            // Pure network error — keep persisted state (offline support)
            const persistedState = get();
            if (persistedState.user) {
              set({ isAuthenticated: true, isLoading: false });
            } else {
              set({ isAuthenticated: false, isLoading: false });
            }
          } else {
            // Server returned any error (401, 403, 404, etc.) — token is invalid for this backend.
            // This clears the stale token so the user is redirected to login to get a fresh one.
            console.log('[BTCCP] Session invalid (' + error.response?.status + ') — clearing for re-login');
            set({ isAuthenticated: false, user: null, isLoading: false });
            clearTokens();
          }
        }
      },

      // Logout — calls backend then clears local state
      logout: async () => {
        try {
          await api.post('/auth/logout');
        } catch (err) {
          // Logout backend call failed (network/token expired) — clear local state anyway
          console.warn('[BTCCP] Backend logout failed:', err?.message || 'unknown');
        }
        clearTokens();
        set({
          user: null,
          isAuthenticated: false,
          isGuest: false,
          error: null,
          mustChangePassword: false,
          forceMemberView: false,
        });
      },

      // Set active org
      setActiveOrg: (orgId, orgSlug) => {
        set({ activeOrgId: orgId, activeOrgSlug: orgSlug });
      },

      isAdmin: () => {
        const { user, forceMemberView } = get();
        if (forceMemberView) return false;
        if (!user) return false;
        if (user.super_admin) return true;
        const membership = user.memberships?.find(
          (m) => m.organization_id === get().activeOrgId || m.slug === get().activeOrgSlug
        );
        // Check org-level admin roles
        if (membership && ['org_owner', 'org_admin', 'tournament_manager'].some((r) =>
          membership.roles?.includes(r)
        )) return true;
        // Check tournament-scoped admin (has admin rights for at least one tournament in this org)
        const orgSlug = get().activeOrgSlug;
        const tournRoles = user.tournament_admin_roles || [];
        if (orgSlug && tournRoles.some(r => r.org_slug === orgSlug)) return true;
        if (!orgSlug && tournRoles.length > 0) return true;
        return false;
      },

      // Returns the tournament this user is admin of (if tournament-scoped admin)
      getTournamentAdminRole: () => {
        const { user } = get();
        const roles = user?.tournament_admin_roles || [];
        const orgSlug = get().activeOrgSlug;
        return roles.find(r => r.org_slug === orgSlug) || null;
      },

      isScorer: () => {
        const { user } = get();
        if (!user) return false;
        if (user.super_admin) return true;
        const membership = user.memberships?.find(
          (m) => m.organization_id === get().activeOrgId || m.slug === get().activeOrgSlug
        );
        if (!membership) return false;
        return membership.roles?.includes('scorer') || get().isAdmin();
      },

      clearError: () => set({ error: null }),
      setForceMemberView: (val) => set({ forceMemberView: val }),

      guestLogin: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await api.post('/auth/guest-login');
          const { tokens, me, active_organization_id } = response.data;
          if (tokens?.access_token) {
            localStorage.setItem('btccp_access_token', tokens.access_token);
          }
          set({
            user: { ...me, is_guest: true },
            isAuthenticated: true,
            isGuest: true,
            isLoading: false,
            error: null,
            activeOrgSlug: 'btcc',
            activeOrgId: active_organization_id || 'org_btcc',
          });
          return { success: true };
        } catch (err) {
          set({ isLoading: false, error: 'Guest login failed' });
          return { success: false };
        }
      },
    }),
    {
      name: 'btccp-auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        activeOrgSlug: state.activeOrgSlug,
        activeOrgId: state.activeOrgId,
        mustChangePassword: state.mustChangePassword,
      }),
    }
  )
);

export default useAuthStore;
